import React, { useState, useCallback, useEffect } from "react";
import { DebugStep, UploadedFile } from "./types";
import { usePyodide } from "./hooks/usePyodide";
import { useCookiePreferences } from "./hooks/useCookiePreferences";
import { useCodePersistence } from "./hooks/useCodePersistence";
import { useDragAndDrop } from "./hooks/useDragAndDrop";
import {
	parseSharedCodeFromURL,
	clearURLHash,
	ParseSharedCodeResult,
} from "./utils";
import {
	Header,
	CodeEditor,
	FrameInspector,
	SidePane,
	OutputPanel,
	InputDialog,
	HelpButton,
	WalkthroughPanel,
	ToastContainer,
	type ToastMessage,
} from "./components/organisms";
import { useWalkthrough } from "./hooks/useWalkthrough";
import { Analytics } from "@vercel/analytics/react";

function App() {
	// User preferences stored in cookies
	const {
		isSidebarCollapsed,
		isConsoleHidden,
		fontSize,
		sidebarWidth,
		editorHeight,
		toggleSidebarCollapsed,
		toggleConsoleHidden,
		increaseFontSize,
		decreaseFontSize,
		resetFontSize,
		updateSidebarWidth,
		updateEditorHeight,
		updatePreference,
	} = useCookiePreferences();

	// Set starter template for new users
	const starterTemplate = `# Welcome to pyBlaze!
# Start writing your Python code here

print("Hello, World!")
print("I'm learning Python!")

# Try these challenges:
# 1. Change the message above
# 2. Add your name: print("My name is YourName")
# 3. Do some math: print(5 + 3)

# Click the green "Run Code" button to see your program work!
# Or try "Learn Mode" to see how it runs step by step`;

	// State for tracking initialization
	const [initializationState, setInitializationState] = useState<{
		hasProcessedURL: boolean;
		sharedCodeResult: ParseSharedCodeResult | null;
		shouldShowWelcome: boolean;
	}>({
		hasProcessedURL: false,
		sharedCodeResult: null,
		shouldShowWelcome: false,
	});

	// Code persistence using cookies - initialize with empty for now
	const { code, setCode } = useCodePersistence();

	// State management
	const [isDebugging, setIsDebugging] = useState(false);
	const [debugSteps, setDebugSteps] = useState<DebugStep[]>([]);
	const [currentStep, setCurrentStep] = useState(0);
	const [uploadedFiles, setUploadedFiles] = useState<Map<string, UploadedFile>>(
		new Map(),
	);
	const [output, setOutput] = useState("");
	const [error, setError] = useState("");
	const [plotImages, setPlotImages] = useState<string[]>([]);

	const [expandedPanel, setExpandedPanel] = useState<
		"editor" | "console" | "inspector" | null
	>(null);
	const [toasts, setToasts] = useState<ToastMessage[]>([]);
	const [hasShownReadyToast, setHasShownReadyToast] = useState(false);

	// Resizing state
	const [isResizing, setIsResizing] = useState(false);
	const [isVerticalResizing, setIsVerticalResizing] = useState(false);

	// Track if user has manually hidden console to respect their choice
	const [hasManuallyHiddenConsole, setHasManuallyHiddenConsole] =
		useState(false);

	// Auto-show console when there's meaningful output and console is hidden
	useEffect(() => {
		// Only show console for meaningful output, not loading messages
		const hasMeaningfulOutput =
			(output && output !== "Executing code...") ||
			error ||
			plotImages.length > 0;

		// Only auto-show if user hasn't manually hidden the console
		if (hasMeaningfulOutput && isConsoleHidden && !hasManuallyHiddenConsole) {
			// Directly update the preference to show console
			updatePreference("consoleHidden", false);
		}
	}, [
		output,
		error,
		plotImages,
		isConsoleHidden,
		hasManuallyHiddenConsole,
		updatePreference,
	]);

	// Pyodide hook
	const {
		isReady,
		status,
		statusText,
		updateStatus,
		runCode,
		startDebugging,
		uploadFile,
		removeFile,
		clearDebugState,
		inputRequest,
		submitInput,
		cancelInput,
	} = usePyodide();

	// Note: Sidebar state is now managed via cookies and respects user preference

	// Toast management
	const addToast = useCallback(
		(
			message: string,
			type: ToastMessage["type"] = "info",
			duration?: number,
		) => {
			const id =
				Date.now().toString() + Math.random().toString(36).substr(2, 9);
			const newToast: ToastMessage = {
				id,
				message,
				type,
				duration,
			};
			setToasts((prev) => [...prev, newToast]);
		},
		[],
	);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	}, []);

	// Fullscreen functionality
	const toggleFullscreen = useCallback(() => {
		if (!document.fullscreenElement) {
			// Enter fullscreen
			document.documentElement
				.requestFullscreen()
				.then(() => {
					addToast(
						"Entered fullscreen mode. Press F11 or ESC to exit.",
						"info",
						3000,
					);
				})
				.catch((err) => {
					console.error("Failed to enter fullscreen:", err);
					addToast("Could not enter fullscreen mode.", "error", 3000);
				});
		} else {
			// Exit fullscreen
			document.exitFullscreen();
		}
	}, [addToast]);

	// Global keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// F11 - Toggle fullscreen
			if (e.key === "F11") {
				e.preventDefault();
				toggleFullscreen();
				return;
			}

			// F - Toggle fullscreen (like YouTube)
			if (
				e.key === "f" &&
				!e.ctrlKey &&
				!e.metaKey &&
				!e.altKey &&
				!e.shiftKey
			) {
				// Only if not typing in an input/textarea
				const target = e.target as HTMLElement;
				if (
					target.tagName !== "INPUT" &&
					target.tagName !== "TEXTAREA" &&
					!target.isContentEditable
				) {
					e.preventDefault();
					toggleFullscreen();
					return;
				}
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [toggleFullscreen]);

	// Show ready toast when Python environment becomes ready
	useEffect(() => {
		if (isReady && !hasShownReadyToast) {
			addToast("Python environment is ready!", "success", 3000);
			setHasShownReadyToast(true);
		}
	}, [isReady, hasShownReadyToast, addToast]);

	// Handle URL parsing and code initialization on mount
	useEffect(() => {
		const initializeApp = async () => {
			// Parse shared code from URL
			const sharedCodeResult = parseSharedCodeFromURL();

			if (sharedCodeResult.success && sharedCodeResult.code) {
				// Load shared code from URL
				setCode(sharedCodeResult.code);
				setInitializationState({
					hasProcessedURL: true,
					sharedCodeResult,
					shouldShowWelcome: false,
				});
			} else if (sharedCodeResult.error) {
				// Failed to parse shared code
				setInitializationState({
					hasProcessedURL: true,
					sharedCodeResult,
					shouldShowWelcome: false,
				});
			} else {
				// No shared code in URL - check if we need welcome screen
				const shouldShowWelcome = !code.trim();
				setInitializationState({
					hasProcessedURL: true,
					sharedCodeResult,
					shouldShowWelcome,
				});

				// Show welcome screen if no code exists
				if (shouldShowWelcome) {
					setCode(starterTemplate);
				}
			}
		};

		// Only run initialization once
		if (!initializationState.hasProcessedURL) {
			initializeApp();
		}
	}, [initializationState.hasProcessedURL, code, setCode, starterTemplate]);

	// Handle shared code from URL and show appropriate toasts
	useEffect(() => {
		if (initializationState.sharedCodeResult?.success) {
			// Clear the URL hash after loading to clean up the address bar
			clearURLHash();
			// Show success message
			addToast("Code loaded from shared link!", "success", 3000);
		} else if (initializationState.sharedCodeResult?.error) {
			// Show error message for failed parsing
			addToast(
				`Failed to load shared code: ${initializationState.sharedCodeResult.error}`,
				"error",
				5000,
			);
			// Clear the URL hash to clean up the address bar
			clearURLHash();
		}
	}, [initializationState.sharedCodeResult, addToast]);

	// Debug functions
	const resetDebugger = useCallback(() => {
		// Clear Python debugging state AND user variables first
		clearDebugState();

		// Then reset React state
		setIsDebugging(false);
		setCurrentStep(0);
		setDebugSteps([]);
		updateStatus("ready", "Ready to inspect");
		setOutput("");
		setError("");
		setPlotImages([]);

		// Reset manual hide flag when resetting
		setHasManuallyHiddenConsole(false);
	}, [updateStatus, clearDebugState]);

	const handleDebugCode = async () => {
		if (!code.trim()) return;

		// Clear any previous debug state AND user variables before starting
		clearDebugState();

		// Reset manual hide flag for new debug session - allow auto-show to work
		setHasManuallyHiddenConsole(false);

		setIsDebugging(true);
		setDebugSteps([]);
		setCurrentStep(0);
		setOutput("");
		setError("");
		setPlotImages([]);

		const result = await startDebugging(code);
		if (result.success) {
			setDebugSteps(result.steps);
			// Start from the very beginning (step 0) to show initial state
			setCurrentStep(0);
		} else {
			setOutput(`Inspect Error: ${result.error}`);
			setError(result.error);
			setIsDebugging(false);
			// Clear debug state on error too
			clearDebugState();
		}
	};

	const stepForward = useCallback(async () => {
		if (!isDebugging || currentStep >= debugSteps.length) return;

		if (currentStep < debugSteps.length - 1) {
			setCurrentStep((prev) => prev + 1);
		} else {
			// Reached the end - run the code to get final output
			updateStatus(
				"finished",
				"Inspect session completed, executing code for output...",
			);

			try {
				const result = await runCode(code);

				if (result.success) {
					setOutput(result.output || "No output produced");
				} else {
					setOutput(`Program finished with error:\n${result.error}`);
				}
			} catch (err: any) {
				setOutput(`Error getting program output: ${err.message}`);
			}

			updateStatus("finished", "Inspect session completed");
		}
	}, [
		isDebugging,
		currentStep,
		debugSteps.length,
		updateStatus,
		runCode,
		code,
	]);

	// Debug navigation functions
	const stepToFirst = useCallback(() => {
		if (!isDebugging || debugSteps.length === 0) return;

		// Go to the very first step (initial state before execution)
		setCurrentStep(0);
	}, [isDebugging, debugSteps]);

	const stepBackward = useCallback(() => {
		if (!isDebugging || currentStep <= 0) return;
		setCurrentStep((prev) => prev - 1);
	}, [isDebugging, currentStep]);

	const stepToLast = useCallback(() => {
		if (!isDebugging || debugSteps.length === 0) return;
		setCurrentStep(debugSteps.length - 1);
	}, [isDebugging, debugSteps.length]);

	const stepToPosition = useCallback(
		(position: number) => {
			if (!isDebugging || debugSteps.length === 0) return;
			const clampedPosition = Math.max(
				0,
				Math.min(position, debugSteps.length - 1),
			);
			setCurrentStep(clampedPosition);
		},
		[isDebugging, debugSteps.length],
	);

	const handleCodeChange = useCallback(
		(newCode: string) => {
			// If code has changed significantly and we're not currently debugging,
			// clear the state to prevent variable persistence
			if (newCode !== code && !isDebugging) {
				clearDebugState();
				setOutput("");
				setError("");
				setPlotImages([]);
			}
			setCode(newCode);
		},
		[code, isDebugging, clearDebugState, setCode],
	);

	const handleRunCode = useCallback(async () => {
		if (!isReady || !code.trim()) return;

		// Clear debug state when switching to normal execution
		clearDebugState();

		// Reset manual hide flag for new execution - allow auto-show to work
		setHasManuallyHiddenConsole(false);

		try {
			setOutput("Executing code...");
			setError("");
			setPlotImages([]);

			const result = await runCode(code);

			if (result.success) {
				setOutput(result.output || "Code executed successfully");
				setPlotImages(result.plotImages || []);
			} else {
				setError(result.error);
				setPlotImages([]);
			}
		} catch (err: any) {
			setError(`Error executing code: ${err.message}`);
			setPlotImages([]);
		}
	}, [isReady, code, runCode, clearDebugState]);

	const handleFileUpload = useCallback(
		async (files: FileList) => {
			const newFiles = new Map(uploadedFiles);

			for (const file of Array.from(files)) {
				try {
					const arrayBuffer = await file.arrayBuffer();
					const uint8Array = new Uint8Array(arrayBuffer);

					const fileData: UploadedFile = {
						name: file.name,
						size: file.size,
						type: file.type,
						data: uint8Array,
					};

					newFiles.set(file.name, fileData);
					uploadFile(file.name, uint8Array);
				} catch (err) {
					console.error(`Failed to upload file ${file.name}:`, err);
				}
			}

			setUploadedFiles(newFiles);
		},
		[uploadedFiles, uploadFile],
	);

	const handleRemoveFile = useCallback(
		(fileName: string) => {
			const newFiles = new Map(uploadedFiles);
			newFiles.delete(fileName);
			setUploadedFiles(newFiles);
			removeFile(fileName);
		},
		[uploadedFiles, removeFile],
	);

	const clearAllFiles = useCallback(() => {
		uploadedFiles.forEach((_, fileName) => {
			removeFile(fileName);
		});
		setUploadedFiles(new Map());
	}, [uploadedFiles, removeFile]);

	const clearOutput = useCallback(() => {
		setOutput("");
		setError("");
		setPlotImages([]);
		// Reset manual hide flag when output is cleared
		setHasManuallyHiddenConsole(false);
	}, []);

	// Custom console toggle that tracks manual hiding
	const handleToggleConsole = useCallback(() => {
		if (!isConsoleHidden) {
			// User is hiding the console manually
			setHasManuallyHiddenConsole(true);
		} else {
			// User is showing the console manually - reset the flag
			setHasManuallyHiddenConsole(false);
		}
		toggleConsoleHidden();
	}, [isConsoleHidden, toggleConsoleHidden]);

	const handleExpandEditor = useCallback(() => {
		setExpandedPanel((prev) => (prev === "editor" ? null : "editor"));
	}, []);

	const handleExpandConsole = useCallback(() => {
		setExpandedPanel((prev) => (prev === "console" ? null : "console"));
	}, []);

	const handleExpandInspector = useCallback(() => {
		setExpandedPanel((prev) => (prev === "inspector" ? null : "inspector"));
	}, []);

	const handleLoadExample = useCallback(
		(exampleCode: string, title: string) => {
			// Clear all state before loading new example
			clearDebugState();
			setCode(exampleCode);
			resetDebugger();
			clearOutput();

			// Show success message
			addToast(`Loaded: ${title}`, "success", 3000);
		},
		[resetDebugger, clearOutput, clearDebugState, setCode, addToast],
	);

	// Loading code from a walkthrough step — quieter than handleLoadExample
	// (the walkthrough panel itself shows what step you're on, so no toast).
	const loadWalkthroughCode = useCallback(
		(stepCode: string) => {
			clearDebugState();
			setCode(stepCode);
			resetDebugger();
			clearOutput();
		},
		[clearDebugState, setCode, resetDebugger, clearOutput],
	);

	const walkthrough = useWalkthrough({ onLoadCode: loadWalkthroughCode });

	const handleViewFile = useCallback(
		(fileName: string, content: string) => {
			// Clear all state before loading new file
			clearDebugState();
			setCode(content);
			resetDebugger();
			clearOutput();

			// Show success message
			addToast(`Viewing: ${fileName}`, "success", 3000);
		},
		[resetDebugger, clearOutput, clearDebugState, setCode, addToast],
	);

	// Sidebar resizing handlers
	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsResizing(true);
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isResizing) return;

			const newWidth = Math.max(180, Math.min(600, e.clientX - 40)); // Min 180px, max 600px, accounting for toggle button width
			updateSidebarWidth(newWidth);
		},
		[isResizing, updateSidebarWidth],
	);

	const handleMouseUp = useCallback(() => {
		if (isResizing) {
			setIsResizing(false);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		}
	}, [isResizing]);

	// Add global mouse event listeners for resizing
	useEffect(() => {
		if (isResizing) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);

			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};
		}
	}, [isResizing, handleMouseMove, handleMouseUp]);

	// Vertical resizing handlers
	const handleVerticalMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsVerticalResizing(true);
		document.body.style.cursor = "row-resize";
		document.body.style.userSelect = "none";
	}, []);

	const handleVerticalMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isVerticalResizing) return;

			const headerHeight = 60; // Header height
			const mainAreaHeight = window.innerHeight - headerHeight;
			const minEditorHeight = 30; // Minimum 30% for editor
			const maxEditorHeight = 80; // Maximum 80% for editor

			const newHeightPercentage = Math.max(
				minEditorHeight,
				Math.min(
					maxEditorHeight,
					((e.clientY - headerHeight) / mainAreaHeight) * 100,
				),
			);

			updateEditorHeight(newHeightPercentage);
		},
		[isVerticalResizing, updateEditorHeight],
	);

	const handleVerticalMouseUp = useCallback(() => {
		if (isVerticalResizing) {
			setIsVerticalResizing(false);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		}
	}, [isVerticalResizing]);

	// Add global mouse event listeners for vertical resizing
	useEffect(() => {
		if (isVerticalResizing) {
			document.addEventListener("mousemove", handleVerticalMouseMove);
			document.addEventListener("mouseup", handleVerticalMouseUp);

			return () => {
				document.removeEventListener("mousemove", handleVerticalMouseMove);
				document.removeEventListener("mouseup", handleVerticalMouseUp);
			};
		}
	}, [isVerticalResizing, handleVerticalMouseMove, handleVerticalMouseUp]);

	// Update output based on current debug step for progressive output display
	useEffect(() => {
		if (
			isDebugging &&
			debugSteps.length > 0 &&
			currentStep >= 0 &&
			currentStep < debugSteps.length
		) {
			const currentDebugStep = debugSteps[currentStep];
			if (currentDebugStep?.output !== undefined) {
				// Show the progressive output up to the current step
				setOutput(currentDebugStep.output || "");
			}
		}
	}, [isDebugging, debugSteps, currentStep]);

	// Drag and drop functionality
	const { isDragOver } = useDragAndDrop({
		onFilesDrop: handleFileUpload,
		onDropSuccess: (fileCount) =>
			addToast(`Dropped ${fileCount} file(s)`, "success", 3000),
	});

	// Cleanup on component unmount
	useEffect(() => {
		return () => {
			// Clear debug state when component unmounts
			clearDebugState();
		};
	}, [clearDebugState]);

	const currentDebugStep =
		isDebugging && debugSteps.length > 0 ? debugSteps[currentStep] : null;

	return (
		<div className="min-h-screen bg-gray-25 dark:bg-gray-900 transition-colors duration-200 cursor-default">
			<Header
				isRunning={status === "debugging" && !isDebugging}
				isDebugging={isDebugging}
				isReady={isReady}
				status={status}
				statusText={statusText}
				onRun={handleRunCode}
				onDebug={handleDebugCode}
				onStop={resetDebugger}
				onStepOver={stepForward}
				onContinue={stepForward}
				onToggleFullscreen={toggleFullscreen}
			/>

			<div className="flex" style={{ height: "calc(100vh - 60px)" }}>
				{/* Sidebar - VS Code Style Side Pane */}
				<div
					className="flex-shrink-0 h-full transition-all duration-300"
					style={{
						width: isSidebarCollapsed ? "48px" : `${sidebarWidth + 48}px`, // +48px for icon bar
					}}
				>
					<SidePane
						files={uploadedFiles}
						onFileUpload={handleFileUpload}
						onRemoveFile={handleRemoveFile}
						onClearAll={clearAllFiles}
						onViewFile={handleViewFile}
						isCollapsed={isSidebarCollapsed}
						onToggleCollapse={toggleSidebarCollapsed}
						sidebarWidth={sidebarWidth}
						onLoadExample={handleLoadExample}
						hasWalkthrough={walkthrough.hasWalkthrough}
						onStartWalkthrough={walkthrough.start}
					/>
				</div>

				{/* Resize Handle - Gap between side pane and editor */}
				{!isSidebarCollapsed && (
					<div
						className={`w-1 cursor-col-resize transition-colors flex-shrink-0 relative ${
							isResizing
								? "bg-blue-500"
								: "bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-500"
						}`}
						onMouseDown={handleMouseDown}
						title="Drag to resize side pane"
					>
						{/* Visual indicator on hover */}
						<div
							className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-8 bg-gray-400 dark:bg-gray-500 rounded-full opacity-0 hover:opacity-100 transition-opacity ${
								isResizing ? "opacity-100 bg-blue-500" : ""
							}`}
						/>
					</div>
				)}

				{/* Main Content Area */}
				<div className="flex-1 flex flex-col bg-gray-25 dark:bg-gray-900 min-w-0 transition-colors duration-200 overflow-hidden">
					<main className="flex-1 flex flex-col min-h-0">
						<div className="flex flex-col min-w-0 h-full">
							{/* Top Area - Code Editor and Frame Inspector */}
							{expandedPanel !== "console" && (
								<div
									className="flex min-w-0 relative"
									style={{
										height:
											expandedPanel === "editor"
												? "100%"
												: isConsoleHidden
													? "100%"
													: `${editorHeight}%`,
									}}
								>
									{/* Code Editor */}
									{expandedPanel !== "inspector" && (
										<div
											className={`${
												isDebugging && expandedPanel !== "editor"
													? "w-3/5"
													: "flex-1"
											} min-h-0`}
										>
											<CodeEditor
												code={code}
												onChange={handleCodeChange}
												isReady={isReady}
												isDebugging={isDebugging}
												currentLine={currentDebugStep?.lineNumber}
												onDebug={handleDebugCode}
												onStep={stepForward}
												onStepBackward={stepBackward}
												onStepToFirst={stepToFirst}
												onStepToLast={stepToLast}
												onStepToPosition={stepToPosition}
												onRun={handleRunCode}
												onReset={resetDebugger}
												canStep={
													isDebugging && currentStep < debugSteps.length - 1
												}
												canStepBack={isDebugging && currentStep > 0}
												status={status}
												statusText={statusText}
												currentStep={currentStep}
												totalSteps={debugSteps.length}
												fontSize={fontSize}
												onIncreaseFontSize={increaseFontSize}
												onDecreaseFontSize={decreaseFontSize}
												onResetFontSize={resetFontSize}
												isExpanded={expandedPanel === "editor"}
												onToggleExpand={handleExpandEditor}
												enableSharing={true}
											/>
										</div>
									)}

									{/* Frame Inspector - Show beside code editor when debugging */}
									{isDebugging && expandedPanel !== "editor" && (
										<div
											className={`${expandedPanel === "inspector" ? "flex-1" : "w-2/5"} min-h-0`}
										>
											<FrameInspector
												debugStep={currentDebugStep}
												isDebugging={isDebugging}
												currentStep={currentStep}
												totalSteps={debugSteps.length}
												fontSize={fontSize}
												isExpanded={expandedPanel === "inspector"}
												onToggleExpand={handleExpandInspector}
											/>
										</div>
									)}
								</div>
							)}

							{/* Horizontal Resize Handle */}
							{expandedPanel === null && !isConsoleHidden && (
								<div
									className={`h-1 cursor-row-resize transition-colors relative flex-shrink-0 ${
										isVerticalResizing
											? "bg-blue-500"
											: "bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-500"
									}`}
									onMouseDown={handleVerticalMouseDown}
									title="Drag to resize"
								>
									{/* Visual indicator */}
									<div
										className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-1 w-8 bg-gray-400 dark:bg-gray-500 rounded-full opacity-0 hover:opacity-100 transition-opacity ${
											isVerticalResizing ? "opacity-100 bg-blue-500" : ""
										}`}
									/>
								</div>
							)}

							{/* Bottom Area - Console */}
							{expandedPanel !== "editor" && expandedPanel !== "inspector" && (
								<div
									className="min-w-0 min-h-0"
									style={{
										height:
											expandedPanel === "console"
												? "100%"
												: isConsoleHidden
													? "auto"
													: `${100 - editorHeight}%`,
									}}
								>
									<OutputPanel
										output={output}
										error={error}
										plotImages={plotImages}
										isReady={isReady}
										onClear={clearOutput}
										fontSize={fontSize}
										isExpanded={expandedPanel === "console"}
										onToggleExpand={handleExpandConsole}
										isHidden={isConsoleHidden}
										onToggleHidden={handleToggleConsole}
									/>
								</div>
							)}
						</div>
					</main>
				</div>

				{/* Walkthrough Panel - slides in on the right when a lesson walkthrough is active */}
				{walkthrough.active && (
					<div className="flex-shrink-0 h-full w-[380px] xl:w-[420px]">
						<WalkthroughPanel
							walkthrough={walkthrough.active}
							stepIndex={walkthrough.stepIndex}
							onNext={walkthrough.next}
							onPrev={walkthrough.prev}
							onGoTo={walkthrough.goTo}
							onLoadStepCode={walkthrough.loadStepCode}
							onRun={handleRunCode}
							onClose={walkthrough.close}
							isReady={isReady}
						/>
					</div>
				)}
			</div>

			{/* Input Dialog for Python input() calls */}
			<InputDialog
				isOpen={!!inputRequest}
				prompt={inputRequest?.prompt || ""}
				onSubmit={submitInput}
				onCancel={cancelInput}
			/>

			{/* Help Button */}
			<HelpButton />

			{/* Drag and Drop Overlay */}
			{isDragOver && (
				<div className="fixed inset-0 z-50 bg-python-blue-500/20 backdrop-blur-sm flex items-center justify-center">
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-dashed border-python-blue-500 p-8 max-w-md mx-4">
						<div className="text-center">
							<div className="w-16 h-16 mx-auto mb-4 rounded-full bg-python-blue-100 dark:bg-python-blue-900/30 flex items-center justify-center">
								<svg
									className="w-8 h-8 text-python-blue-600 dark:text-python-blue-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
									></path>
								</svg>
							</div>
							<h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
								Drop Files Here
							</h3>
							<p className="text-gray-600 dark:text-gray-400 text-sm">
								Release to upload files to the file manager
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Toast Notifications */}
			<ToastContainer toasts={toasts} onRemoveToast={removeToast} />
			<Analytics />
		</div>
	);
}

export default App;
